/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, timer, from } from 'rxjs';
import { tap, take, catchError, switchMap } from 'rxjs/operators';
import { LoggingService, Logger, DateHandlerService } from 'crimson';
import { StarfleetService, StarfleetConfig, StarfleetTransitionState } from 'starfleet';
import { isEqual } from 'lodash';
import {
    PlaytestState,
    ActiveTestDataType,
    ResultState,
    TestState,
    PlaytestMenu,
    PlaytestType,
    TestStats,
    TestStatsData,
    TEST_STATS_KEY
} from '../../shared';
import { NvHttpEndpoint, NvHttpEndpointFactoryService, NvRequestOptions } from 'nv-angular-http-endpoint';
import { Configuration } from '../../shared/configuration/Configuration';

const nvidiaLogo = '../../assets/img/DefaultGameArt-BoxArt.jpg';
const lcarsBaseUrl = 'https://img.nvidiagrid.net';
const ptmsPingDuration = 60000; // 60 seconds

interface GridServerInfo {
    requestStatus?: {
        serverId?: string;
    };
}

interface GridServiceUrls {
    gfnServiceInfo?: {
        gfnServiceEndpoints?: {
            loginProvider: string;
            streamingServiceUrl: string;
        }[];
    };
}

@Injectable({
    providedIn: 'root'
})
export class PlaytestLiveService {
    private logger: Logger;
    private starfleetIdToken: string = null;
    private playtesterVerifyCode: string = null;
    private testArrayAll = null;
    private playtesterSignedIn = new BehaviorSubject<PlaytestState>(PlaytestState.none);
    private testPreparedState = new BehaviorSubject<ResultState>(ResultState.unknown);
    private playtestMenuOption: PlaytestMenu;
    protected headers: any = {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json;charset=utf-8'
    };
    private endPoint: NvHttpEndpoint;
    private readonly RETRY_COUNT = 1;
    private shortlistedTestId;
    private currentPlaytestState = PlaytestState.none;
    private heartbeatSubscription = undefined;
    private timerSource = undefined;
    private heartbeatCount = 0;
    private userDisplayName: string;
    private activeTestArray: ActiveTestDataType[] = [];
    private invitationStatus: string;
    private invitationErrorText: string;
    private testStats: TestStats = {};

    constructor(
        private appConfig: Configuration,
        private starfleetConfig: StarfleetConfig,
        private loggingService: LoggingService,
        private starfleetService: StarfleetService,
        private endpointFactory: NvHttpEndpointFactoryService,
        private dateHandlerService: DateHandlerService
    ) {
        this.logger = this.loggingService.getLogger('PlaytestLiveService');
        this.playtesterVerifyCode = null;
        this.shortlistedTestId = null;
        this.playtestMenuOption = PlaytestMenu.playtest;
    }

    private createEndpoint(): NvHttpEndpoint {
        return this.endpointFactory.createEndpoint({
            config: {
                defaultTimeout: 10000,
                defaultTimeBetweenRetries: 5000,
                defaultRetries: 1
            }
        });
    }

    init() {
        this.starfleetService.initialize(true, true);
        this.starfleetIdToken = null;
        this.logger.info('Playtester Service Initialization - Success');
        this.subscribeAndFetchStarfleetToken();
    }

    subscribeAndFetchStarfleetToken() {
        this.starfleetService.isUserLoggedIn.pipe(take(1)).subscribe(loggedIn => {
            if (loggedIn) {
                this.starfleetService
                    .getAuthToken(false)
                    .pipe(take(1))
                    .subscribe(authToken => {
                        if (authToken) {
                            this.logger.info('AUTH: Fetched starfleet auth token, processing');
                            if (!this.starfleetIdToken) {
                                this.starfleetIdToken = authToken;
                                // Store the token into local storage
                                this.setDataToLocalStorage('idToken', authToken);
                                this.decodeJWTToken(authToken);
                                this.readTestStats();
                                this.fetchTestData();
                                this.startHeartbeatPing();
                                this.setCurrentPageState(PlaytestState.home);
                                this.checkInvites();
                            }
                        } else {
                            this.logger.info('AUTH: No token, return to login');
                            this.setCurrentPageState(PlaytestState.login);
                        }
                    });
            } else {
                this.setCurrentPageState(PlaytestState.login);
            }
        });
    }

    // This activity should be done when we have idToken available (either through starfleet login or fetching from local storage)
    private fetchTestData() {
        this.getAllTests()
            .pipe(take(1))
            .subscribe(result => {
                if (!result) {
                    return;
                }
                const testArrayAll = result.filter(test => test?.testId !== '' && test?.gameInfo?.name !== '');
                const sortedTests = this.sortTestWithTimeStamp(testArrayAll, 'startTime');
                if (!isEqual(sortedTests, this.testArrayAll)) {
                    this.testArrayAll = sortedTests;
                    this.processTestData();
                } else {
                    const activeTestArray = [...this.activeTestArray];
                    this.refreshTestPlaytestStatus();
                    if (!isEqual(activeTestArray, this.activeTestArray)) {
                        this.setCurrentPageState(PlaytestState.home);
                    }
                }

                // Cleanup test stats to remove tests that are no longer active
                let testRemoved = false;
                Object.keys(this.testStats).forEach(testId => {
                    if (!this.activeTestArray.find(activeTest => activeTest.testId === testId)) {
                        delete this.testStats[testId];
                        testRemoved = true;
                    }
                });
                if (testRemoved) {
                    this.setTestStats();
                }
            });
    }

    login() {
        if (this.starfleetConfig.sameTabLogin) {
            const returnLocationUrl = window.location.href;
            const sfLogout = this.starfleetService.login({ force: true, returnLocationUrl }).subscribe(state => {
                this.logger.info('Starfleet login state:' + state);
                this.setCurrentPageState(PlaytestState.login);
            });
            sfLogout?.unsubscribe();
            this.setCurrentPageState(PlaytestState.none);
        } else {
            const sfLogout = this.starfleetService.login({ force: true }).subscribe(state => {
                this.logger.info('Starfleet login state:' + state);
                switch (state) {
                    case StarfleetTransitionState.LoginCompleted:
                        this.subscribeAndFetchStarfleetToken();
                        break;
                    case StarfleetTransitionState.LoginAbortedByUser:
                    case StarfleetTransitionState.LoginAbortedAlreadyLoggedIn:
                    case StarfleetTransitionState.LoginFailed:
                        this.setCurrentPageState(PlaytestState.login);
                        break;
                    case StarfleetTransitionState.LoginFailedNoRetry:
                    case StarfleetTransitionState.LoginFailedToStart:
                        this.logger.error('Fatal login error:' + state);
                        break;
                }
            });
            sfLogout?.unsubscribe();
            // Tabbed login should probably transition to login-in-progress state?
            // this.setCurrentPageState(PlaytestState.loginInProgress);
        }
    }

    logout(): void {
        if (this.heartbeatSubscription) {
            this.heartbeatSubscription.unsubscribe();
        }
        this.timerSource = null;
        this.starfleetService.deleteClientData(this.starfleetIdToken);
        this.starfleetIdToken = null;
        this.userDisplayName = '';

        const starfleetLogout = this.starfleetService.logout('').subscribe({
            next: state => {
                this.logger.info('Starfleet logout state:' + state);
                if (state === StarfleetTransitionState.LogoutFailed) {
                    // This error is expected until logout path is fixed
                    this.logger.info('Executing Starfleet error recovery');
                    this.starfleetService
                        .refreshUser()
                        .pipe(take(1))
                        .subscribe(_ => {
                            starfleetLogout.unsubscribe();
                            this.setCurrentPageState(PlaytestState.login);
                        });
                } else if (state === StarfleetTransitionState.LogoutCompleted) {
                    starfleetLogout.unsubscribe();
                    this.setCurrentPageState(PlaytestState.login);
                }
            },
            error: error => {
                this.logger.error('Starfleet logout error:' + error);
            }
        });
        this.setCurrentPageState(PlaytestState.none);
        return;
    }

    forceLogout(): void {
        if (this.heartbeatSubscription) {
            this.heartbeatSubscription.unsubscribe();
        }
        this.timerSource = null;
        this.starfleetService.deleteClientData(this.starfleetIdToken);
        this.starfleetIdToken = null;
        this.userDisplayName = '';

        this.setCurrentPageState(PlaytestState.login);
    }

    setPlaytesterVerifyCode(code: string) {
        this.playtesterVerifyCode = code;
    }

    getPlaytesterVerifyCode() {
        return this.playtesterVerifyCode;
    }

    private checkInvites() {
        const urlQueryString = window.location.search;
        const urlParams = new URLSearchParams(urlQueryString);
        const invitationCode = urlParams.get('invitation');
        const orgId = urlParams.get('orgId');

        if (orgId && invitationCode) {
            const url = `${this.appConfig.vdkPlaytestConfig.ptmsUrl}/v2/org/${orgId}/testers/team/redeem/${invitationCode}`;
            this.endPoint = this.createEndpoint();
            const options: NvRequestOptions = {
                headers: {
                    Authorization: `Bearer ${this.starfleetIdToken}`
                }
            };

            // Trigger playtester/verify endpoint
            this.endPoint
                .post<any>(url, options)
                .pipe(take(1))
                .subscribe(
                    response => {
                        this.logger.info('Playtest redeem invitation');
                        this.invitationStatus = 'good';
                        this.invitationErrorText = '';
                        this.setCurrentPageState(PlaytestState.invitationRedeemed);
                        this.fetchTestData();
                        return response;
                    },
                    error => {
                        this.logger.info('Error: Playtest redeem invitation', error);
                        this.invitationStatus = 'error';
                        this.invitationErrorText = error?.rawResponse?.error?.detail;
                        this.setCurrentPageState(PlaytestState.invitationRedeemed);
                        return error;
                    }
                );
        }
    }

    public getInvitationState(): string {
        return this.invitationStatus;
    }

    public getInvitationErrorText(): string {
        return this.invitationErrorText;
    }

    public clearInvitation(): void {
        this.invitationStatus = '';
        this.invitationErrorText = '';
    }

    private decodeJWTToken(idtoken: string): any {
        let payload: any;
        try {
            payload = idtoken.split('.')[1];
            payload = JSON.parse(window.atob(payload));
        } catch {
            return undefined;
        }

        if (payload.preferred_username && payload.preferred_username !== '') {
            this.userDisplayName = payload.preferred_username;
        } else if (payload.email && payload.email !== '') {
            this.userDisplayName = payload.email.substr(0, payload.email.indexOf('@'));
        } else {
            this.userDisplayName = 'null';
        }
        return payload;
    }

    getUserDisplayName() {
        return this.userDisplayName;
    }

    private startHeartbeatPing(): void {
        if (!this.timerSource) {
            // Coordinate refresh with the local clock's next minute change
            const now = Date.now();
            let delay = Math.ceil(now / 60000) * 60000 - now;
            if (delay < 1000) {
                delay += 60000; // Less than 1s, add another minute
            }
            this.heartbeatCount = 0;
            this.timerSource = timer(delay, ptmsPingDuration);
            this.logger.info('Delay playtest refresh for ' + (delay / 1000).toString() + 's');
            this.heartbeatSubscription = this.timerSource.subscribe(_ => {
                this.ptmsHeartbeatPing();
            });
        }
    }

    private ptmsHeartbeatPing() {
        this.heartbeatCount++;
        if (this.heartbeatCount > 60 * 6) {
            // 6 hours
            const url = window.location.href.split('?')[0];
            window.location.replace(url);
        }

        this.fetchTestData();
    }

    private getAllTests(): Observable<any> {
        if (!this.starfleetIdToken) {
            this.logger.info('Calling with invalid starfleet token, ending heartbeat');
            this.forceLogout();
            return of(null);
        }
        const url = `${this.appConfig.vdkPlaytestConfig.ptmsUrl}/v3/playtest/tests`;
        this.endPoint = this.createEndpoint();
        return this.endPoint
            .get<any>(url, {
                headers: {
                    Authorization: `Bearer ${this.starfleetIdToken}`
                }
            })
            .pipe(
                catchError(error => {
                    this.logger.info('getAllTests failed:' + error);
                    if (error.status === 401) {
                        this.starfleetIdToken = null;
                        this.logger.info('getAllTests attempting token refresh');
                        this.starfleetService
                            .getAuthToken(true)
                            .pipe(take(1))
                            .subscribe(
                                authToken => {
                                    if (authToken) {
                                        this.logger.info('getAllTests Fetched starfleet auth token, processing');
                                        this.starfleetIdToken = authToken;
                                        // Store the token into local storage
                                        this.setDataToLocalStorage('idToken', authToken);
                                        this.decodeJWTToken(authToken);
                                        return this.getAllTests();
                                    }
                                    this.logger.error('getAllTests Failed to get starfleet auth token');
                                    throw error;
                                },
                                refreshError => {
                                    this.logger.info('getAllTests could not refresh token, exiting');
                                    throw refreshError;
                                }
                            );
                    } else {
                        this.logger.info('getAllTests errored, exiting');
                        this.starfleetIdToken = null;
                    }
                    throw error;
                })
            );
    }

    private updatePlaytestInviteStatus(testId: string, status: string): Observable<any> {
        const url = `${this.appConfig.vdkPlaytestConfig.ptmsUrl}/v2/playtest/test/${testId}/playtesterstatus?status=${status}`;
        this.endPoint = this.createEndpoint();
        const options: NvRequestOptions = {
            headers: {
                Authorization: `Bearer ${this.starfleetIdToken}`
            }
        };

        // Trigger playtester/verify endpoint
        return this.endPoint.put<any>(url, options).pipe(
            tap(
                response => {
                    this.logger.info('Playtest status update - Success');
                    return response;
                },
                error => {
                    this.logger.error('Error: Playtest status update failed with error: ', error);
                    throw error;
                }
            )
        );
    }

    public getLaunchUrls(testId: string): Observable<any> {
        const url = `${this.appConfig.vdkPlaytestConfig.ptmsUrl}/v2/playtest/test/${testId}/deeplinks`;
        this.endPoint = this.createEndpoint();
        const options: NvRequestOptions = {
            headers: {
                Authorization: `Bearer ${this.starfleetIdToken}`
            }
        };

        // Trigger playtester/verify endpoint
        return this.endPoint.get<any>(url, options).pipe(
            tap(
                response => {
                    return response;
                },
                error => {
                    this.logger.error('Error: Playtest launch urls failed with error: ', error);
                    throw error;
                }
            )
        );
    }

    public addCheckCapacity(testId: string): Observable<any> {
        const test = this.testArrayAll.find(element => element.testId === testId);
        if (!test) {
            this.logger.error('Could not find test id');
            return from([{ capacityOk: 'error' }]);
        }
        const orgId = test.organization ?? 'error';
        const url = `${this.appConfig.vdkPlaytestConfig.ptmsUrl}/v2/playtest/tests/${orgId}/test/${testId}/addcheckcapacity`;
        this.endPoint = this.createEndpoint();
        const options: NvRequestOptions = {
            headers: {
                Authorization: `Bearer ${this.starfleetIdToken}`
            }
        };

        // Trigger playtester/verify endpoint
        return this.endPoint.post<any>(url, options).pipe(
            catchError(err => {
                this.logger.error('Error: add check capacity: ', err);
                return from([{ capacityOk: 'error' }]);
            }),
            tap(response => {
                return response;
            })
        );
    }

    processTestStatusChange(testId, response: string) {
        let status = 'none';
        const testIdx = this.activeTestArray.findIndex(testData => testData.testId === testId);
        if (response === 'accept') {
            status = 'accepted';
        } else if (response === 'decline') {
            status = 'denied';
        } else if (response === 'toggle') {
            // Let user accept or decline the status using UI option
            this.activeTestArray[testIdx].status = 'pending';
            this.setCurrentPageState(PlaytestState.home);
        }
        if (status === 'accepted' || status === 'denied') {
            this.updatePlaytestInviteStatus(testId, status)
                .pipe(take(1))
                .subscribe(
                    result => {
                        this.activeTestArray[testIdx].status = status;
                        this.setCurrentPageState(PlaytestState.home);
                    },
                    error => {
                        this.logger.info('Error in updating test status. ErrorText: ', error);
                    }
                );
        }
    }

    private fetchGameAssets(cmsId: string[]) {
        const endpoint = this.createEndpoint();
        const grid = this.appConfig.grid;
        return endpoint.get<GridServiceUrls>(grid.serviceUrlsUrl, { includeRequestId: true }).pipe(
            switchMap(serviceUrls => {
                const streamingServiceUrl = serviceUrls?.gfnServiceInfo?.gfnServiceEndpoints?.find(
                    provider => provider.loginProvider === 'NVIDIA'
                )?.streamingServiceUrl;
                if (!streamingServiceUrl) {
                    throw new Error('Service URLs are missing the NVIDIA streaming endpoint.');
                }
                const serverInfoUrl = `${streamingServiceUrl.replace(/\/$/, '')}/${grid.version}/serverInfo`;
                return endpoint.get<GridServerInfo>(serverInfoUrl, { includeRequestId: true }).pipe(
                    catchError(error => {
                        if (!grid.server) {
                            throw error;
                        }
                        const fallbackUrl = `${grid.server.replace(/\/$/, '')}/${grid.version}/serverInfo`;
                        if (fallbackUrl === serverInfoUrl) {
                            throw error;
                        }
                        this.logger.info('Discovered serverInfo request failed; trying the configured fallback.');
                        return endpoint.get<GridServerInfo>(fallbackUrl, { includeRequestId: true });
                    })
                );
            }),
            switchMap(serverInfo => {
                const vpcId = serverInfo?.requestStatus?.serverId;
                if (!vpcId) {
                    throw new Error('Server info is missing the VPC ID.');
                }
                const url = this.constructLCARSQuery(cmsId, vpcId);
                if (!url) {
                    return of('error');
                }
                return endpoint.get<any>(url, {
                    headers: {
                        Authorization: `GFNJWT ${this.starfleetIdToken}`,
                        'nv-env': `${this.appConfig.lcars.stageHeaderValue}`,
                        'nv-client-id': this.appConfig.lcars.clientId,
                        'nv-cascade-content': 'LIVE',
                        'content-type': 'application/graphql',
                        'nv-client-type': 'BROWSER',
                        'accept-language': 'en-US'
                    }
                });
            }),
            catchError(error => {
                this.logger.error('Error fetching game assets: ', error);
                this.setCurrentPageState(PlaytestState.home);
                throw error;
            })
        );
    }

    constructLCARSQuery(appIdArray: string[], vpcId: string) {
        const uniqueVariants = [
            ...new Set(appIdArray.map(appId => parseInt(appId, 10)).filter(appId => !isNaN(appId)))
        ];
        if (uniqueVariants.length === 0) {
            return null;
        }
        const queryContent = `query PlaytestPortalBoxArt {
  apps(vpcId: ${JSON.stringify(vpcId)}, language: "en_US", country: "ALL", filters: { variants: { id: { in: [${uniqueVariants.join(', ')}] } } }) {
    items {
      id
      images {
        GAME_BOX_ART
      }
      variants {
        id
      }
    }
  }
}`;
        return `${this.appConfig.lcars.serverUrl}?requestType=appMetaData&query=${encodeURIComponent(queryContent)}`;
    }

    sortTestWithTimeStamp(testArray, criteriaTime) {
        const inputTestArray = testArray;
        if (testArray?.length >= 2) {
            inputTestArray.sort((a, b) => {
                if (criteriaTime === 'startTime') {
                    return Date.parse(a?.timeWindow?.start) - Date.parse(b?.timeWindow?.start);
                } else if (criteriaTime === 'endTime') {
                    return Date.parse(a?.timeWindow?.end) - Date.parse(b?.timeWindow?.endTime);
                } /* creationTime */ else {
                    return Date.parse(a?.creationTime) - Date.parse(b?.creationTime);
                }
            });
        }
        return inputTestArray;
    }

    getTestActiveState(startTime: Date, endTime: Date): TestState {
        let testState: TestState = TestState.unknown;
        const nowTime = Date.now();
        const testEndTime = endTime.getTime();
        const testStartTime = startTime.getTime();

        if (testEndTime > nowTime && nowTime >= testStartTime) {
            // Test's start time is in past and end time is not yet arrived. So it is an active test NOW
            testState = TestState.playnow;
        } else if (testEndTime > nowTime && nowTime < testStartTime) {
            testState = TestState.upcoming;
        } else if (nowTime >= testEndTime) {
            testState = TestState.expired;
        }
        return testState;
    }

    isTestActiveOrUpcoming(): TestState {
        let retState = TestState.unknown;
        if (this.activeTestArray.length > 0) {
            for (let idx = 0; idx < this.activeTestArray.length; idx++) {
                if (
                    this.activeTestArray[idx].playStatus === TestState.playnow &&
                    this.activeTestArray[idx].status === 'accepted'
                ) {
                    // If we detect a test as playNow among all tests, no need to process further,
                    // that means we are not showing upcoming test tile.
                    retState = TestState.playnow;
                    return retState;
                } else if (
                    this.activeTestArray[idx].playStatus === TestState.upcoming &&
                    this.activeTestArray[idx].status === 'accepted'
                ) {
                    retState = TestState.upcoming;
                }
            }
        }
        return retState;
    }

    /* If no test is for play NOW, get earliest upcoming test */
    getEarliestUpcomingTestId() {
        const nowTime = Date.now();
        let earliestTestTime = null;
        let retTestId = null;
        this.activeTestArray.forEach(testData => {
            if (testData.status === 'accepted' && testData.playStatus === TestState.upcoming) {
                const endTime = testData.endTime.getTime();
                const startTime = testData.startTime.getTime();

                if (!earliestTestTime) {
                    earliestTestTime = testData.startTime;
                    retTestId = testData.testId;
                } else if (startTime > nowTime && endTime > nowTime) {
                    if (earliestTestTime < startTime) {
                        earliestTestTime = testData.startTime;
                        retTestId = testData.testId;
                    }
                }
            }
        });
        return retTestId;
    }

    private processTestData() {
        this.activeTestArray = [];
        if (this.testArrayAll.length > 0) {
            const sortedTests = this.testArrayAll;
            const cmsIdList: string[] = [];
            // Filter only Active tests
            sortedTests.forEach(test => {
                if (test?.timeWindow?.start && test?.timeWindow?.end) {
                    const start = new Date(test?.timeWindow?.start);
                    const end = new Date(test?.timeWindow?.end);
                    const cmsId = parseInt(test?.gameInfo?.CMSID);
                    // Exclude any test with incorrect cmsId format
                    const invalidCmsId = isNaN(cmsId);
                    if (!invalidCmsId) {
                        if (this.getTestActiveState(start, end) === TestState.playnow) {
                            this.setTestInfo(test, TestState.playnow);
                            // this.logger.info('PlayNow tests: ', test);
                            // get cmsId List to fetch all assets at once
                            cmsIdList.push(test?.gameInfo?.CMSID);
                        } else if (this.getTestActiveState(start, end) === TestState.upcoming) {
                            this.setTestInfo(test, TestState.upcoming);
                            cmsIdList.push(test?.gameInfo?.CMSID);
                        }
                    }
                }
            });

            // Fetch game assets for all games with unique variant id
            const uniqueVariants = [...new Set(cmsIdList)];
            if (uniqueVariants.length > 0) {
                this.fetchGameAssets(uniqueVariants)
                    .pipe(take(1))
                    .subscribe(
                        result => {
                            // Assign GAME_BOX_ART to all active tests (if fetched with LCARS query)
                            if (result !== 'error') {
                                const appsItemCount = result?.data?.apps?.items ? result.data.apps.items.length : 0;
                                if (appsItemCount) {
                                    this.activeTestArray.forEach((testData, testIdx) => {
                                        result.data.apps.items.forEach(itemData => {
                                            if (itemData?.variants.length) {
                                                if (
                                                    itemData.variants.find(
                                                        variantData => variantData?.id === testData?.cmsId
                                                    )
                                                ) {
                                                    let gameBoxArtImage = itemData?.images?.GAME_BOX_ART;
                                                    if (gameBoxArtImage) {
                                                        if (
                                                            !this.isUrl(gameBoxArtImage) &&
                                                            !gameBoxArtImage.includes(lcarsBaseUrl)
                                                        ) {
                                                            gameBoxArtImage = `${lcarsBaseUrl}/${gameBoxArtImage}`;
                                                        }
                                                        this.activeTestArray[testIdx].gameThumbnailUrl =
                                                            gameBoxArtImage;
                                                    }
                                                }
                                            }
                                        });
                                    });
                                }
                            }
                        },
                        error => {
                            this.logger.info('LCARS: Error in fetching the game assets with error: ', error);
                        }
                    );
            }
        }
        this.setCurrentPageState(PlaytestState.home);
    }

    private isUrl(string) {
        let url;

        try {
            url = new URL(string);
        } catch (_) {
            return false;
        }

        return url.protocol === 'http:' || url.protocol === 'https:';
    }

    public isDuplicateTest(testId: string) {
        const dupTest = this.activeTestArray.find(element => element.testId === testId);
        if (dupTest) {
            this.logger.info('Test is duplicate .... skip adding into active test array');
            return true;
        }
        return false;
    }

    public setCurrentPageState(newState: PlaytestState) {
        switch (newState) {
            case PlaytestState.login:
            case PlaytestState.home:
            case PlaytestState.settings:
            case PlaytestState.invites:
            case PlaytestState.launch:
            case PlaytestState.invitationRedeemed:
            case PlaytestState.invitationError:
                this.currentPlaytestState = newState;
                break;
            default:
                this.currentPlaytestState = PlaytestState.none;
        }
        this.playtesterSignedIn.next(this.currentPlaytestState);
    }

    public getCurrentPageState() {
        return this.currentPlaytestState;
    }

    public getPageState(): Observable<PlaytestState> {
        return this.playtesterSignedIn.asObservable();
    }

    // Create active tests objects with initializations
    private setTestInfo(testData: any, playStatus: TestState) {
        this.testPreparedState.next(ResultState.initiated);
        const activeTest: ActiveTestDataType = {
            coordEmail: '',
            playtesterId: '',
            gameThumbnailUrl: '',
            startTimeStr: '',
            endTimeStr: '',
            testId: '',
            testName: '',
            name: '',
            status: '',
            cmsId: '',
            gameId: '',
            starfleetToken: '',
            nativeUrl: '',
            browserUrl: '',
            playStatus: TestState.unknown,
            startTime: new Date(),
            endTime: new Date(),
            notes: '',
            testType: PlaytestType.Normal,
            tags: []
        };

        activeTest.testId = testData?.testId;
        activeTest.testName = testData?.name;
        activeTest.name = testData?.gameInfo?.name;
        activeTest.status = testData?.playtester[0]?.status;
        activeTest.cmsId = testData?.gameInfo?.CMSID;
        activeTest.gameId = testData?.gameInfo.gameId;
        activeTest.starfleetToken = this.starfleetIdToken;
        activeTest.playStatus = playStatus;
        activeTest.gameThumbnailUrl = nvidiaLogo;
        activeTest.startTime = testData?.timeWindow ? new Date(testData?.timeWindow?.start) : new Date();
        activeTest.startTimeStr = this.dateHandlerService.formatDate(activeTest.startTime, 'MMM d, yyyy p');
        activeTest.endTime = testData?.timeWindow ? new Date(testData?.timeWindow?.end) : new Date();
        activeTest.endTimeStr = this.dateHandlerService.formatDate(activeTest.endTime, 'MMM d, yyyy p');
        activeTest.playtesterId = this.getUserDisplayName();
        activeTest.coordEmail = testData?.coordinator[0] ? testData?.coordinator[0].emailId : '';
        activeTest.status = testData?.playtester[0]?.status;
        activeTest.notes = testData?.notes;
        activeTest.testType = testData?.testType as PlaytestType;
        activeTest.tags = testData?.tags;

        // Push this configured active test to test and deeplinks array
        this.activeTestArray.push(activeTest);
        // enable to launch HOME page now.
    }

    getPlayNowTestData(filterByTag?: string) {
        if (filterByTag) {
            return this.activeTestArray.filter(test => test.tags.includes(filterByTag));
        }
        return this.activeTestArray;
    }

    private refreshTestPlaytestStatus() {
        this.activeTestArray.forEach((testData, testIdx) => {
            const startTime = testData.startTime;
            const endTime = testData.endTime;
            const testState = this.getTestActiveState(startTime, endTime);
            if (testState === TestState.playnow) {
                this.activeTestArray[testIdx].playStatus = TestState.playnow;
            } else if (testState === TestState.upcoming) {
                this.activeTestArray[testIdx].playStatus = TestState.upcoming;
            } else if (testState === TestState.expired) {
                // Remove element from the active tests
                this.activeTestArray.splice(testIdx, 1);
            }
        });
    }

    isActiveTestIdShortlisted() {
        if (this.shortlistedTestId) {
            return true;
        }
        return false;
    }

    setLaunchTestId(testId) {
        this.shortlistedTestId = testId;
    }

    getLaunchTestId() {
        return this.shortlistedTestId;
    }

    setDataToLocalStorage(key: string, value: any) {
        if (window.localStorage) {
            window.localStorage.setItem(key, value);
        } else {
            this.logger.info('Error: LocalStorage is not available to set a value');
        }
    }

    getDataFromLocalStorage(key: string) {
        if (window.localStorage) {
            const item = window.localStorage.getItem(key);
            return item;
        } else {
            this.logger.info('Error: LocalStorage is not available to get a value');
            return null;
        }
    }

    getTestHomeMenuOption() {
        return this.playtestMenuOption;
    }

    setTestHomeMenuOption(menuOption: PlaytestMenu) {
        this.playtestMenuOption = menuOption;
    }

    private readTestStats() {
        const testStatsRaw = this.getDataFromLocalStorage('testStats');
        if (testStatsRaw) {
            this.testStats = JSON.parse(testStatsRaw);
        } else {
            this.testStats = {};
        }
    }

    getTestStats(testId: string): TestStatsData {
        if (this.testStats[testId]) {
            return this.testStats[testId];
        }
        return {
            totalRuns: 0
        };
    }

    private setTestStats() {
        this.setDataToLocalStorage(TEST_STATS_KEY, JSON.stringify(this.testStats));
    }

    incrementTestRunCount(testId: string) {
        if (!this.testStats[testId]) {
            this.testStats[testId] = {
                totalRuns: 1
            };
        } else {
            this.testStats[testId].totalRuns++;
        }
        this.setTestStats();
    }
}
